import { useApi } from '@src/app/context/Api';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useState } from 'react';

import { Styles } from './includes/styles';

/**
 * DeleteNote
 *
 * Displays a modal to delete a note on the risk record.
 *
 * @param eid
 * @param handleSearch
 * @param isOpen
 * @param note
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function DeleteNote({ eid, handleSearch, isOpen, note, setIsOpen }) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { clearApiCache, useApiMutation } = useApi('RiskRecord');
  const [RiskRecordNoteUpdate] = useApiMutation('RiskRecordNoteUpdate');

  const { toast } = useToast();

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @returns {Promise<boolean>}
   */
  const handleSubmit = async () => {
    setIsUpdating(true);

    await RiskRecordNoteUpdate(
      {
        eid,
        note: '',
      },
      {
        onError: () => {
          toast.show({
            message: 'Unable to Delete Note.',
            title: 'Delete Note Failed',
            variant: 'error',
          });
        },
        onSuccess: async () => {
          setIsUpdating(false);
          setIsOpen(false);
          await clearApiCache([{ apiKey: 'RiskRecord', queryKeys: ['GetRiskRecord'] }]);
          handleSearch();
          toast.show({
            message: 'Note has been successfully deleted.',
            title: 'Delete note to EID',
            variant: 'success',
          });
        },
      }
    );
  };

  return (
    <ErrorHandler location="src/features/EidSearch/DeleteNote/DeleteNote.jsx">
      <Modal
        isOpen={isOpen}
        onClose={() => {
          return setIsOpen(false);
        }}
        title="Delete Note"
      >
        <Modal.Section>
          <Styles>
            <Grid>
              <Grid.Col span={{ xs: 12 }}>
                <Text>
                  <pre
                    style={{
                      fontFamily: 'var(--abyss-fonts-primary)',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                    }}
                  >
                    {note}
                  </pre>
                </Text>
              </Grid.Col>
            </Grid>
          </Styles>
        </Modal.Section>
        <Modal.Section>
          <Divider height={1} />
          <Layout.Group alignLayout="right">
            <Button
              onClick={() => {
                setIsOpen(false);
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              ariaLoadingLabel="Deleting Note"
              isLoading={isUpdating}
              onClick={handleSubmit}
              variant="destructive"
            >
              Delete
            </Button>
          </Layout.Group>
        </Modal.Section>
      </Modal>
    </ErrorHandler>
  );
}
